import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { loadFeaturedProjects } from "./lib/projects";
import { ProjectCard } from "./components/ProjectCard";
import { ProjectsPage } from "./pages/ProjectsPage.tsx";
import { ProjectPage } from "./pages/ProjectPage";
import { Analytics } from "@vercel/analytics/react";
import { DocumentHead } from "./components/DocumentHead";

import "./styles/tailwind.css";

const featuredProjects = loadFeaturedProjects();

function App() {
  return (
    <BrowserRouter>
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>
      <HeadMetadata />
      <DocumentHead />
      <Analytics />
      <ScrollToTopOnRouteChange />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/:id" element={<ProjectPage />} />
        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}

function HeadMetadata() {
  useEffect(() => {
    const ensureLink = (attrs: Record<string, string>) => {
      const selector = Object.entries(attrs)
        .map(([k, v]) => `[${k}="${v}"]`)
        .join("");
      let el = document.head.querySelector<HTMLLinkElement>(`link${selector}`);
      if (!el) {
        el = document.createElement("link");
        Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
        document.head.appendChild(el);
      } else {
        Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
      }
    };

    const ensureMeta = (attrs: Record<string, string>) => {
      const name = attrs.name;
      let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
        document.head.appendChild(el);
      } else {
        Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
      }
    };

    ensureLink({ rel: "icon", type: "image/png", href: "/favicon-96x96.png", sizes: "96x96" });
    ensureLink({ rel: "icon", type: "image/svg+xml", href: "/favicon.svg" });
    ensureLink({ rel: "shortcut icon", href: "/favicon.ico" });
    ensureLink({ rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" });
    ensureLink({ rel: "manifest", href: "/site.webmanifest" });
    ensureMeta({ name: "apple-mobile-web-app-title", content: "Alan Viles" });
    ensureMeta({ name: "apple-mobile-web-app-capable", content: "yes" });
    ensureMeta({ name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" });
    ensureMeta({ name: "theme-color", content: "#101525" });
  }, []);

  return null;
}

function ScrollToTopOnRouteChange() {
  const { pathname, hash } = useLocation();

  useLayoutEffect(() => {
    if (hash) return;

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname, hash]);

  return null;
}

function HomePage() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#projects") {
      const el = document.getElementById("projects");
      el?.scrollIntoView({ behavior: "instant" });
    }
  }, [location.hash]);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenis.on("scroll", (e: { scroll: number }) => setScrollProgress(e.scroll));
    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  return (
    <div className="app">
      <div className="bg-layer" aria-hidden="true" />
      <main id="main-content" tabIndex={-1}>
        <Hero />
        <About />
        <ProjectsPreview scrollProgress={scrollProgress} />
        <Experience scrollProgress={scrollProgress} />
        <Contact />
      </main>
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="app">
      <div className="bg-layer" aria-hidden="true" />
      <main id="main-content" tabIndex={-1}>
        <section className="relative full-viewport screen-stage safe-gutter flex flex-col items-center justify-center text-center overflow-hidden">
          <div className="not-found-orbit" aria-hidden="true" />
          <div className="not-found-card panel-card">
            <span className="section-kicker font-mono text-[0.7rem] tracking-[0.2em] text-accent block mb-4">Signal lost</span>
            <p className="font-mono text-text-muted text-sm mb-4">Error 404</p>
            <h1 className="font-[family-name:var(--font-display)] text-[clamp(2.5rem,8vw,5rem)] leading-[1.05] tracking-[-0.02em] text-text text-balance mb-4">
              Page not found
            </h1>
            <Link to="/" className="home-return-button">
              Back to home
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}

function ProjectsPreview({ scrollProgress }: { scrollProgress: number }) {
  const sectionRef = useRef<HTMLElement>(null);
  const projectRefs = useRef<(HTMLElement | null)[]>([]);
  const desktopSectionRef = useRef<HTMLElement>(null);
  const [projectProgress, setProjectProgress] = useState<number[]>(() => featuredProjects.map(() => 0));
  const [sectionProgress, setSectionProgress] = useState(0);
  useReveal(sectionRef);

  const updateProgress = useCallback(() => {
    const viewport = window.innerHeight;

    const sectionEl = desktopSectionRef.current;
    if (sectionEl) {
      const rect = sectionEl.getBoundingClientRect();
      const range = sectionEl.offsetHeight - viewport;
      setSectionProgress(
        range > 0 ? Math.max(0, Math.min(1, -rect.top / range)) : 0
      );
    }

    const newProgress = featuredProjects.map((_, i) => {
      const el = projectRefs.current[i];
      if (!el) return 0;
      const rect = el.getBoundingClientRect();
      const range = el.offsetHeight - viewport;
      return range > 0 ? Math.max(0, Math.min(1, -rect.top / range)) : 0;
    });
    setProjectProgress(newProgress);
  }, [scrollProgress]);

  useEffect(() => {
    updateProgress();
  }, [updateProgress]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const ro = new ResizeObserver(updateProgress);
    ro.observe(section);
    return () => ro.disconnect();
  }, [updateProgress]);

  const projects = featuredProjects;

  return (
    <section ref={sectionRef} id="projects" className="projects p-0">
      <div className="safe-gutter py-16 max-w-[1200px] mx-auto pt-8 md:hidden">
        <span className="section-kicker font-mono text-[0.7rem] tracking-[0.2em] text-accent block mb-4">Portfolio</span>
        <h2 className="font-[family-name:var(--font-display)] text-[clamp(1.5rem,4vw,2.5rem)] mb-8 text-balance">Projects</h2>
        <Link to="/projects" className="font-mono text-[0.7rem] tracking-[0.2em] text-accent mb-8 block hover:underline w-fit">
          View all projects →
        </Link>
      </div>

      {/* Desktop: side by side in one sticky section */}
      <section ref={desktopSectionRef} className="min-h-[200vh] p-0 hidden md:block">
        <div className="sticky top-0 screen-stage flex flex-col justify-center safe-gutter">
          <div className="max-w-[1200px] mx-auto w-full">
            <div className="mb-6">
              <span className="section-kicker font-mono text-[0.7rem] tracking-[0.2em] text-accent block mb-4">Portfolio</span>
              <h2 className="font-[family-name:var(--font-display)] text-[clamp(1.5rem,4vw,2.5rem)] mb-4 text-balance">Projects</h2>
              <Link to="/projects" className="font-mono text-[0.7rem] tracking-[0.2em] text-accent block hover:underline w-fit">
                View all projects →
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {projects.map((p, i) => (
                <Link key={p.id} to={`/projects/${p.id}`} state={{ from: "home" }} className="block">
                  <ProjectCard project={p} onClick={() => {}} index={i} scrollProgress={sectionProgress} />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mobile: stacked with individual pause */}
      <div className="max-w-[1200px] mx-auto block md:hidden">
        {projects.map((p, i) => (
          <section
            key={p.id}
            ref={(el) => { projectRefs.current[i] = el; }}
            className="min-h-[200vh] p-0"
          >
            <div className="sticky top-0 screen-stage flex flex-col justify-center safe-gutter">
              <Link to={`/projects/${p.id}`} state={{ from: "home" }} className="block w-full max-w-[400px] mx-auto">
                <ProjectCard project={p} onClick={() => {}} index={i} scrollProgress={projectProgress[i] ?? 0} />
              </Link>
            </div>
          </section>
        ))}
      </div>
    </section>
  );
}

function Hero() {
  return (
    <section className="relative full-viewport screen-stage safe-gutter flex flex-col items-center justify-center text-center">
      <div className="relative">
        <h1 className="hero-title mb-1">Alan Viles</h1>
        <span className="section-kicker font-mono text-[0.7rem] tracking-[0.2em] text-accent mb-4 block uppercase">
          University of Canterbury
        </span>
        <p className="text-lg text-text-dim mb-8 tracking-tight">
          Embedded systems <span className="mx-1 text-accent/70">&middot;</span> Electronics{" "}
          <span className="mx-1 text-accent/70">&middot;</span> Software
        </p>
      </div>
      <div
        className="scroll-cue absolute inset-x-0 flex flex-col items-center gap-1"
        style={{
          bottom: "var(--bottom-chrome-space)",
        }}
      >
        <span className="font-mono text-[0.65rem] tracking-[0.2em] text-accent">Scroll</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 stroke-accent">
          <path d="M12 5v14M5 12l7 7 7-7" />
        </svg>
      </div>
    </section>
  );
}

function useReveal(ref: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ob = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.1, rootMargin: "0px 0px -80px" }
    );
    el.classList.add("reveal");
    ob.observe(el);
    return () => ob.disconnect();
  }, [ref]);
}

function About() {
  const ref = useRef<HTMLElement>(null);
  useReveal(ref);
  const skills = ["Embedded Systems", "Arduino", "Python", "Observational Astronomy"];
  return (
    <section ref={ref} className="about min-h-[200vh] p-0">
      <div className="sticky top-0 screen-stage safe-gutter flex flex-col justify-center max-w-[1200px] mx-auto">
        <span className="section-kicker font-mono text-[0.7rem] tracking-[0.2em] text-accent block mb-4">Bio</span>
        <h2 className="font-[family-name:var(--font-display)] text-[clamp(1.5rem,4vw,2.5rem)] mb-8 text-balance">About</h2>
        <div className="flex flex-wrap gap-8">
          <div>
            <p className="text-text-dim text-lg leading-relaxed max-w-[38rem]">
              I study electrical and computer engineering at the University of Canterbury, with work that runs from
              analog sensing and Arduino firmware to React apps backed by live data. Recent coursework includes a track counter for trail monitoring; outside class I build rockets with UC Aerospace,
              guide stargazing tours, play hockey, and ship small tools like an aurora forecaster.
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            {skills.map((skill) => (
              <span key={skill} className="px-6 py-4 font-mono text-sm border border-[var(--color-border)] text-accent">
                {skill}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

interface ExperienceItemData {
  id: string;
  company: string;
  role: string;
  dates?: string;
  description: string;
  image?: string;
  link?: string;
  linkText?: string;
  links?: { href: string; label: string }[];
}

const EXPERIENCE_ITEMS: ExperienceItemData[] = [
  {
    id: "jaycar",
    company: "Jaycar Electronics",
    role: "Retail Sales Assistant",
    dates: "Oct 2026 to present",
    description:
      "Shop-floor sales at Jaycar Christchurch: help customers choose components, kits, tools, and test equipment, and answer questions about how the parts fit a project.",
    links: [{ href: "https://www.jaycar.co.nz/", label: "Jaycar \u2197" }],
  },
  {
    id: "TTT",
    company: "University of Canterbury",
    role: "Outreach Assistant, Townsend Teece Telescope Observer",
    dates: "Winter 2026",
    description:
      "Run public observing nights at the Townsend Teece Telescope, measured by steady turnout and clear sky briefings, by setting up the dome, operating the telescope, and walking guests through what they are seeing.",
    image: "/IMG_4699.jpg",
    links: [
      { href: "https://www.canterbury.ac.nz/research/research-facilities-and-equipment/townsend-teece-telescope", label: "Telescope Information ➚" },
      { href: "https://www.artscentre.org.nz/whats-on/heritage-stargazing-2026/", label: "Heritage Stargazing ➚" },
    ]
  },
  {
    id: "photography",
    company: "alanvilesphotography",
    role: "Freelance Photographer",
    dates: "Jan 2023 to present",
    description:
      "Freelance landscape and night sky photography: timelapse, aurora, sports, and headshots.",
    image: "/Crater_Telescope.jpg",
    links: [
      { href: "https://alanvilesphotography.com/", label: "Portfolio ➚" },
      { href: "https://www.instagram.com/alanvilesphotography/", label: "Instagram ➚" },
    ],
  },
  {
    id: "darksky",
    company: "Dark Sky Project",
    role: "Astronomy Guide",
    dates: "Sep 2022 to Jan 2025",
    description:
      "Guided night sky tours at Tekapo: laser constellation talks, telescope operation, and Q&A for international visitors.",
    image: "/DSP.jpg",
  },
  {
    id: "sailgp",
    company: "SailGP",
    role: "Shore Team Assistant, France",
    dates: "March 2024",
    description:
      "Shore team support for SailGP France during the Inspire Careers programme: F50 rigging, maintenance, logistics, and safety checks before races on the waterfront in Lyttelton",
    image: "/Frenchboat.jpg",
  },
];

function Experience({ scrollProgress }: { scrollProgress: number }) {
  const sectionRef = useRef<HTMLElement>(null);
  const [beamProgress, setBeamProgress] = useState(0);

  const updateBeamProgress = useCallback(() => {
    const section = sectionRef.current;
    if (!section) return;

    const rect = section.getBoundingClientRect();
    const viewport = window.innerHeight;
    const trackableDistance = rect.height - viewport;

    if (trackableDistance <= 0) {
      setBeamProgress(rect.top < viewport ? 1 : 0);
      return;
    }

    setBeamProgress(Math.max(0, Math.min(1, -rect.top / trackableDistance)));
  }, []);

  useEffect(() => {
    updateBeamProgress();
  }, [scrollProgress, updateBeamProgress]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new ResizeObserver(updateBeamProgress);
    observer.observe(section);
    return () => observer.disconnect();
  }, [updateBeamProgress]);

  return (
    <section ref={sectionRef} className="experience experience-timeline p-0">
      <div className="safe-gutter max-w-[1200px] mx-auto">
        <div className="experience-timeline__intro">
          <span className="section-kicker font-mono text-[0.7rem] tracking-[0.2em] text-accent block mb-4">Work</span>
          <h2 className="font-[family-name:var(--font-display)] text-[clamp(1.5rem,4vw,2.5rem)] mb-4 text-balance">
            Experience
          </h2>
        </div>

        <div className="experience-timeline__track" style={{ "--beam-progress": beamProgress } as React.CSSProperties}>
          <div className="experience-timeline__line" aria-hidden="true">
            <div className="experience-timeline__beam" />
          </div>

          {EXPERIENCE_ITEMS.map((item) => (
            <article key={item.id} className="experience-timeline__entry">
              <div className="experience-timeline__date">
                <span>{item.dates}</span>
              </div>

              <div
                className={`experience-timeline__content panel-card${item.image ? "" : " experience-timeline__content--copy-only"}`}
              >
                {item.image && (
                  <div className="experience-timeline__visual">
                    <img src={item.image} alt={item.company} />
                  </div>
                )}
                <div className="experience-timeline__copy">
                  <span className="experience-timeline__company font-mono">{item.company}</span>
                  <h3>{item.role}</h3>
                  <p>{item.description}</p>
                  {item.links && item.links.length > 0 ? (
                    <div className="experience-timeline__links">
                      {item.links.map((link) => (
                        <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer">
                          {link.label}
                        </a>
                      ))}
                    </div>
                  ) : (
                    item.link && (
                      <div className="experience-timeline__links">
                        <a href={item.link} target="_blank" rel="noopener noreferrer">
                          {item.linkText ?? "Visit \u2197"}
                        </a>
                      </div>
                    )
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

interface ContactLinkData {
  href: string;
  label: string;
  isExternal?: boolean;
}

const CONTACT_LINKS: ContactLinkData[] = [
  { href: "mailto:alan@viles.nz", label: "Email" },
  { href: "https://github.com/alanviles", label: "GitHub", isExternal: true },
  { href: "https://www.linkedin.com/in/alan-viles/", label: "LinkedIn", isExternal: true },
];

function Contact() {
  const ref = useRef<HTMLElement>(null);
  useReveal(ref);
  return (
    <section ref={ref} className="contact min-h-[200vh] p-0 pt-16 text-center">
      <div className="sticky top-0 screen-stage safe-gutter flex flex-col justify-center max-w-[1200px] mx-auto text-center">
        <span className="section-kicker font-mono text-[0.7rem] tracking-[0.2em] text-accent block mb-4">Contact</span>
        <h2 className="font-[family-name:var(--font-display)] text-[clamp(1.5rem,4vw,2.5rem)] mb-6 text-balance">Get in touch</h2>
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {CONTACT_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              target={link.isExternal ? "_blank" : undefined}
              rel={link.isExternal ? "noopener noreferrer" : undefined}
              className="contact-link"
            >
              {link.label}
            </a>
          ))}
        </div>
        <p className="text-xs text-text-muted font-mono">© {new Date().getFullYear()} Alan Viles</p>
      </div>
    </section>
  );
}

const root = createRoot(document.getElementById("root")!);
root.render(<App />);

export default App;
